import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { 
  registerServiceWorker, 
  requestNotificationPermission, 
  subscribeToPushNotifications,
  setupNotificationPermissionMonitoring,
  checkNotificationPermission,
  renewNotificationPermission
} from './lib/notifications';
import ErrorBoundary from './components/ErrorBoundary';
import { UpdateAvailableBanner } from './components/UpdateAvailableBanner';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import AddTransaction from './pages/AddTransaction';
import TopUp from './pages/TopUp';
import AddCustomer from './pages/AddCustomer';
import CustomerDetails from './pages/CustomerDetails';
import UpdateProfile from './pages/UpdateProfile';
import Welcome from './pages/Welcome';
import ResetPassword from './pages/ResetPassword';
import UpdatePassword from './pages/UpdatePassword';
import Dashboard from './pages/Dashboard';
import { supabase } from './lib/supabase';
import PrintableStatement from './pages/PrintableStatement';
import InvitePage from './pages/InvitePage';
import TestOpenAI from './pages/TestOpenAI';

function App() {
  useEffect(() => {
    const setupPushNotifications = async () => {
      try {
        // Only setup if not in StackBlitz or WebContainer
        if (!window.location.hostname.includes('stackblitz') && 
            !window.location.hostname.includes('webcontainer')) {
          
          console.log('Setting up push notifications...');
          
          // Check current notification status
          const { permission, needsRenewal, hasValidSubscription } = await checkNotificationPermission();
          
          console.log('Notification status:', { permission, needsRenewal, hasValidSubscription });
          
          if (permission === 'granted') {
            // If permission is granted but we need renewal (lost subscription)
            if (needsRenewal) {
              console.log('Permission granted but subscription lost, renewing...');
              const renewed = await renewNotificationPermission();
              if (renewed) {
                console.log('Notification subscription renewed successfully');
              } else {
                console.warn('Failed to renew notification subscription');
              }
            } else if (hasValidSubscription) {
              console.log('Notifications already set up correctly');
            } else {
              // Try to set up notifications normally
              try {
                const registration = await registerServiceWorker();
                const subscription = await subscribeToPushNotifications(registration);
                console.log('Push subscription setup completed:', !!subscription);
              } catch (error) {
                console.warn('Failed to setup notifications:', error);
              }
            }
          } else if (permission === 'default') {
            // If permission is default, we can request it
            console.log('Notification permission is default, will request when appropriate');
          } else {
            console.log('Notification permission denied, skipping setup');
          }
          
          // Start monitoring notification permissions
          const stopMonitoring = setupNotificationPermissionMonitoring();
          
          // Cleanup monitoring when component unmounts
          return stopMonitoring;
        }
      } catch (error) {
        console.error('Error setting up push notifications:', error);
      }
    };

    // Only setup notifications if we're not in an iframe
    if (!window.frameElement) {
      setupPushNotifications();
    }
  }, []);

  // Route guard for dashboard
  const ProtectedDashboard = () => {
    const [isAllowed, setIsAllowed] = React.useState<boolean | null>(null);

    React.useEffect(() => {
      const checkAccess = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            setIsAllowed(false);
            return;
          }

          const { data: profile, error } = await supabase
            .from('profiles')
            .select('phone, account_type')
            .eq('id', user.id)
            .single();

          if (error) throw error;

          // Check both phone number and account type
          setIsAllowed(profile?.phone === '7331533033' && profile?.account_type === 'admin');
        } catch (err) {
          console.error('Error checking dashboard access:', err);
          setIsAllowed(false);
        }
      };

      checkAccess();
    }, []);

    if (isAllowed === null) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-gray-600">جاري التحميل...</div>
        </div>
      );
    }

    return isAllowed ? <Dashboard /> : <Navigate to="/home" replace />;
  };

  return (
    <ErrorBoundary>
      <Router>
        {/* Update Available Banner */}
        <UpdateAvailableBanner />
        
        <Routes>
          {/* Main App Routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/home" element={<Home />} />
          <Route path="/add-transaction" element={<AddTransaction />} />
          <Route path="/top-up" element={<TopUp />} />
          <Route path="/add-customer" element={<AddCustomer />} />
          <Route path="/customer/:id" element={<CustomerDetails />} />
          <Route path="/update-profile" element={<UpdateProfile />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/printable-statement/:id" element={<PrintableStatement />} />
          <Route path="/invite/:code" element={<InvitePage />} />
          <Route path="/test-openai" element={<TestOpenAI />} />
          
          {/* Protected Dashboard Route */}
          <Route path="/dashboard" element={<ProtectedDashboard />} />
          
          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;